import { toast } from "sonner";
import { useServices } from "../services/hooks/useServices";
import { PlacementType } from "../types/inboxPlacements.types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DateRange } from "react-day-picker";
import { EmailProvider } from "@/types/inboxPlacements.types";
import { AuthProvider } from "@/types/common";

const QUERY_KEY = "inboxplacements";
const QUERY_KEY_PROVIDER_BREAKDOWN = "inboxplacements-provider-breakdown";
export const QUERY_KEY_TEST_HISTORY = "inboxplacements-test-history";
// -------------------- Retrieval Methods --------------------

export const useGetInboxPlacements = (
  params: {
    type?: PlacementType;
    page?: number;
    limit?: number;
  } = { page: 1, limit: 10 }
) => {
  const { inboxPlacementService } = useServices();

  return useQuery({
    queryKey: [QUERY_KEY, params],
    queryFn: () => inboxPlacementService.getInboxPlacements(params),
    refetchOnWindowFocus: false,
  });
};

export const useGetCardSummary = (type: PlacementType) => {
  const { inboxPlacementService } = useServices();
  return useQuery({
    queryKey: [QUERY_KEY, type],
    queryFn: () => inboxPlacementService.getCardSummary(type),
    refetchOnWindowFocus: false,
  });
};
export const useGetTestResult = (
  email: string,
  type: PlacementType,
  testId: string
) => {
  const { inboxPlacementService } = useServices();
  return useQuery({
    queryKey: [QUERY_KEY, email, type, testId],
    queryFn: () => inboxPlacementService.getTestResult(email, type, testId),
    refetchOnWindowFocus: false,
  });
};

export const useGetProviderBreakdown = (email: string, testId: string) => {
  const { inboxPlacementService } = useServices();
  return useQuery({
    queryKey: [QUERY_KEY_PROVIDER_BREAKDOWN, email, testId],
    queryFn: () => inboxPlacementService.getProviderBreakdown(email, testId),
    enabled: !!email && !!testId,
    refetchOnWindowFocus: false,
  });
};

export const useGetTestHistory = (
  email: string,
  dateRange: DateRange,
  page: number,
  limit: number,
  type: string
) => {
  const { inboxPlacementService } = useServices();
  return useQuery({
    queryKey: [QUERY_KEY_TEST_HISTORY, email, dateRange, page, limit, type],
    queryFn: () =>
      inboxPlacementService.getTestHistory(email, dateRange, page, limit, type),
    enabled: !!email,
    refetchOnWindowFocus: false,
  });
};

export const useInboxPlacement = (
  domainSearchTerm?: string,
  templateSearchTerm?: string,
  espType?: AuthProvider
) => {
  const { inboxPlacementService } = useServices();

  const { data: mailbox, isLoading: mailboxLoading } = useQuery({
    queryKey: ["mailbox", domainSearchTerm, espType],
    queryFn: () => inboxPlacementService.getMailbox(domainSearchTerm, espType),
  });

  const { data: template, isLoading: templateLoading } = useQuery({
    queryKey: ["template", templateSearchTerm],
    queryFn: () => inboxPlacementService.getTemplate(templateSearchTerm),
  });

  return {
    mailbox,
    template,
    mailboxLoading,
    templateLoading,
  };
};

export const useCheckPlacementTestExists = (email: string) => {
  const { inboxPlacementService } = useServices();
  return useQuery({
    queryKey: ["checkPlacementTestExists", email],
    queryFn: () => inboxPlacementService.checkPlacementTestExists(email),
    enabled: !!email,
    refetchOnWindowFocus: false,
  });
};

export const useWarmups = () => {
  const { inboxPlacementService } = useServices();

  return useQuery({
    queryKey: ["warmups"],
    queryFn: () => inboxPlacementService.getWarmups(),
    refetchOnWindowFocus: false,
  });
};

export const usePlacementAccounts = (type: string) => {
  const { inboxPlacementService } = useServices();
  return useQuery({
    queryKey: ["placementAccounts", type],
    queryFn: () => inboxPlacementService.getPlacementAccounts(type),
    refetchOnWindowFocus: false,
  });
};

export const useEmailDetails = (
  testId: string,
  provider: EmailProvider,
  email: string
) => {
  const { inboxPlacementService } = useServices();
  return useQuery({
    queryKey: ["emailDetails", testId, provider, email],
    queryFn: () =>
      inboxPlacementService.getEmailDetails(testId, provider, email),
    enabled:
      !!testId &&
      !!provider &&
      !!email &&
      testId !== "" &&
      provider !== EmailProvider.OTHER &&
      email !== "",
  });
};

export function useScheduledPlacementTestMutations() {
  const { inboxPlacementService } = useServices();

  const queryClient = useQueryClient();

  const { mutateAsync: createScheduledTest, isPending: isCreating } =
    useMutation({
      mutationFn: async ({
        mailbox,
        template,
        providers,
        type,
        espType,
        scheduleType,
        recurrenceType,
        startTime,
        customRecurrence,
      }: {
        mailbox: string[];
        template: string;
        providers: string[];
        type: string;
        espType: AuthProvider;
        scheduleType: "once" | "recurring";
        recurrenceType: string;
        startTime: Date;
        customRecurrence?: {
          interval: number;
          unit: string;
          endType: string;
          endDate?: Date;
          occurrences?: number;
          weekdays?: number[];
        };
      }) => {
        return inboxPlacementService.createScheduledTest(
          mailbox,
          template,
          providers,
          type,
          espType,
          {
            type: scheduleType,
            recurrenceType,
            startTime,
            customRecurrence,
          }
        );
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["scheduledEmailTests"],
        });
        toast.success("Test scheduled successfully!");
      },
      onError: () => {
        toast.error("Failed to schedule test");
      },
    });

  const { mutateAsync: deleteScheduledTest, isPending: isDeleting } =
    useMutation({
      mutationFn: (id: string) => inboxPlacementService.deleteScheduledTest(id),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["scheduledEmailTests"],
        });
        toast.success("Test deleted successfully!");
      },
      onError: () => {
        toast.error("Failed to delete test");
      },
    });

  const { mutateAsync: updateScheduledTest, isPending: isUpdating } =
    useMutation({
      mutationFn: ({
        id,
        mailbox,
        template,
        type,
        scheduleType,
        recurrenceType,
        startTime,
        customRecurrence,
      }: {
        id: string;
        mailbox: string[];
        template: string;
        type: string;
        scheduleType: "once" | "recurring";
        recurrenceType: string;
        startTime: Date;
        customRecurrence?: {
          interval: number;
          unit: string;
          endType: string;
          endDate?: Date;
          occurrences?: number;
          weekdays?: number[];
        };
      }) => {
        return inboxPlacementService.updateScheduledTest(
          id,
          mailbox,
          template,
          type,
          {
            scheduleType,
            recurrenceType,
            startTime,
            customRecurrence,
          }
        );
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["scheduledEmailTests"],
        });
        toast.success("Test updated successfully!");
      },
      onError: () => {
        toast.error("Failed to update test");
      },
    });

  return {
    createScheduledTest,
    isCreating,
    deleteScheduledTest,
    isDeleting,
    updateScheduledTest,
    isUpdating,
  };
}

export const useScheduledPlacementTests = () => {
  const { inboxPlacementService } = useServices();
  return useQuery({
    queryKey: ["scheduledEmailTests"],
    queryFn: () => inboxPlacementService.getScheduledTests(),
  });
};

// ------------------------- Publish Inbox Placement Report -------------------------

export function usePublishInboxPlacementReport() {
  const { inboxPlacementService } = useServices();

  return useMutation({
    mutationFn: async (payload: { ids: string[] }) => {
      return inboxPlacementService.publishInboxPlacementReport(payload.ids);
    },
  });
}

export function useUnPublishInboxPlacementReport() {
  const { inboxPlacementService } = useServices();

  return useMutation({
    mutationFn: async (payload: { ids: string[] }) => {
      return inboxPlacementService.unPublishInboxPlacementReport(payload.ids);
    },
  });
}

export function usePublishAllInboxPlacementReport() {
  const { inboxPlacementService } = useServices();

  return useMutation({
    mutationFn: async (payload: { emails: string[]; type: string }) => {
      return inboxPlacementService.publishAllInboxPlacementReport(
        payload.emails,
        payload.type
      );
    },
  });
}
export function useUnPublishAllInboxPlacementReport() {
  const { inboxPlacementService } = useServices();

  return useMutation({
    mutationFn: async (payload: { emails: string[]; type: string }) => {
      return inboxPlacementService.unPublishAllInboxPlacementReport(
        payload.emails,
        payload.type
      );
    },
  });
}

export function useCheckBulkPlacementReportStatus(
  testIds: string[],
  enabled: boolean = true
) {
  const { inboxPlacementService } = useServices();

  return useQuery({
    queryKey: ["checkBulkPlacementReportStatus"],
    queryFn: () =>
      inboxPlacementService.checkBulkPlacementReportStatus(testIds),
    enabled: !!testIds && testIds.length > 0 && enabled,
    refetchOnWindowFocus: false,
    refetchInterval: 10000, // 10 seconds
  });
}
